import { SpaceContext } from "components/Space/SpaceChat/types";
import useSpaceStore from "stores/spaceStore";
import { Command, SelfContainedCommand, CommandExecutionContext } from "types/commands";
import { 
  StandardToolResult, 
  createSuccessResult, 
  createErrorResult 
} from "types/standardToolResults";
import { getPDFiumStore } from "./store/pdfiumStore";
import { pdfProcessCommand } from './commands/pdf_process';

// Import file service to read PDF files directly

import { createFileLogger } from '../../utils/logger';
const log = createFileLogger('PDFIUM_COMMANDS');

// Check if a value exists in an object of any type


// Utility function to chunk text content
const chunkText = (text: string, chunkSize: number = 1000, overlap: number = 100): string[] => {
  if (text.length <= chunkSize) {
    return [text];
  }
  
  const chunks: string[] = [];
  let start = 0;
  
  while (start < text.length) {
    const end = Math.min(start + chunkSize, text.length);
    const chunk = text.slice(start, end);
    chunks.push(chunk);
    
    // Move start position with overlap
    start = end - overlap;
    if (start >= text.length) break;
  }
  
  return chunks;
};

// Utility function to search for text within content
const findTextOccurrences = (text: string, searchTerm: string, contextSize: number = 200): Array<{text: string, position: number}> => {
  const results: Array<{text: string, position: number}> = [];
  const searchTermLower = searchTerm.toLowerCase();
  const textLower = text.toLowerCase();
  
  let position = 0;
  while (position < text.length) {
    const index = textLower.indexOf(searchTermLower, position);
    if (index === -1) break;
    
    const start = Math.max(0, index - contextSize);
    const end = Math.min(text.length, index + searchTerm.length + contextSize);
    const context = text.slice(start, end);
    
    results.push({
      text: context,
      position: index
    });
    
    position = index + searchTerm.length;
  }
  
  return results;
};

// Import word_import command
import { wordImportCommand } from '../DocsApp/commands/word_import';

// Create a registry of all available commands
export const commands: (Command | SelfContainedCommand)[] = [
  pdfProcessCommand as SelfContainedCommand,
  wordImportCommand as SelfContainedCommand,
  {
    id: 'pdf_createHighlight',
    description: 'Create a highlight in a PDF document',
    autonomyLevel: 'auto',
    visualTreatment: 'narrative',
    
    schema: {
      parameters: [
        {
          name: 'pdfId',
          type: 'string',
          description: 'The PDF entity ID',
          required: true
        },
        {
          name: 'pageIndex',
          type: 'number',
          description: 'The page index (0-based)',
          required: true
        },
        {
          name: 'startIndex',
          type: 'number',
          description: 'Start character index',
          required: true
        },
        {
          name: 'endIndex',
          type: 'number',
          description: 'End character index',
          required: true
        },
        {
          name: 'color',
          type: 'string',
          description: 'Highlight color: yellow, green, blue, pink, or red',
          required: true
        },
        {
          name: 'text',
          type: 'string',
          description: 'The highlighted text',
          required: true
        },
        {
          name: 'comment',
          type: 'string',
          description: 'Optional comment on the highlight',
          required: false
        }
      ]
    },
    
    examples: {
      good: [
        {
          input: { pdfId: 'pdf-123', pageIndex: 0, startIndex: 100, endIndex: 200, color: 'yellow', text: 'important finding' },
          expectedOutput: createSuccessResult('Highlight created on page 1', { highlightId: 'h-123', text: 'important finding' }),
          description: 'Create a yellow highlight on the first page'
        }
      ],
      bad: [
        {
          input: { pdfId: 'pdf-123', pageIndex: 0, startIndex: 200, endIndex: 100, color: 'yellow', text: 'text' },
          error: 'Invalid indices',
          why: 'startIndex must be less than endIndex',
          fix: 'Ensure startIndex < endIndex'
        }
      ]
    },
    
    handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
      try {
        const { pdfId, pageIndex, startIndex, endIndex, color, text, comment } = params;
        
        // Get PDFium store (requires active window context)
        const activeWindowId = context.spaceContext?.activeWindowId;
        const spaceId = context.spaceId;
        
        if (!activeWindowId || !spaceId) {
          return createErrorResult('No active PDF window found', 'No active PDF window in context', 'execution');
        }
        
        const pdfiumStore = getPDFiumStore(activeWindowId, spaceId);
        const store = pdfiumStore.getState();
        
        // Create highlight
        const highlight = await store.actions.createHighlight({
          pageIndex,
          startIndex,
          endIndex,
          color,
          text,
          comment
        });
        
        return createSuccessResult(`Highlight created on page ${pageIndex + 1}`, {
          highlightId: highlight._id,
          text: text.substring(0, 100) + (text.length > 100 ? '...' : '')
        });
      } catch (error: any) {
        log.error('[pdf_createHighlight] Error:', error);
        return createErrorResult('Failed to create highlight', error.message || String(error), 'execution');
      }
    }
  } as SelfContainedCommand,
  {
    id: 'pdf_getHighlights',
    description: 'Get all highlights from a PDF document',
    autonomyLevel: 'auto',
    visualTreatment: 'narrative',
    
    schema: {
      parameters: [
        {
          name: 'pdfId',
          type: 'string',
          description: 'The PDF entity ID',
          required: true
        }
      ]
    },
    
    examples: {
      good: [
        {
          input: { pdfId: 'pdf-123' },
          expectedOutput: createSuccessResult('Found 5 highlights', { count: 5, highlights: [] }),
          description: 'Get all highlights from a PDF'
        }
      ],
      bad: []
    },
    
    handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
      try {
        const { pdfId } = params;
        
        // Get PDFium store
        const activeWindowId = context.spaceContext?.activeWindowId;
        const spaceId = context.spaceId;
        
        if (!activeWindowId || !spaceId) {
          return createErrorResult('No active PDF window found', 'No active PDF window in context', 'execution');
        }
        
        const pdfiumStore = getPDFiumStore(activeWindowId, spaceId);
        const store = pdfiumStore.getState();
        
        // Get all highlights
        const highlights = store.getAllHighlights();
        
        const highlightSummaries = highlights.map(h => ({
          id: h._id,
          pageIndex: h.skeleton.pageIndex,
          color: h.skeleton.color,
          text: h.skeleton.text.substring(0, 100) + (h.skeleton.text.length > 100 ? '...' : ''),
          comment: h.skeleton.comment || null,
          createdAt: h.skeleton.createdAt
        }));
        
        return createSuccessResult(`Found ${highlights.length} highlights`, {
          count: highlights.length,
          highlights: highlightSummaries
        });
      } catch (error: any) {
        log.error('[pdf_getHighlights] Error:', error);
        return createErrorResult('Failed to get highlights', error.message || String(error), 'execution');
      }
    }
  } as SelfContainedCommand,
  {
    id: 'pdf_deleteHighlight',
    description: 'Delete a highlight from a PDF document',
    autonomyLevel: 'confirm',
    visualTreatment: 'narrative',
    
    schema: {
      parameters: [
        {
          name: 'highlightId',
          type: 'string',
          description: 'The highlight entity ID to delete',
          required: true
        }
      ]
    },
    
    examples: {
      good: [
        {
          input: { highlightId: 'h-123' },
          expectedOutput: createSuccessResult('Highlight deleted successfully', { highlightId: 'h-123' }),
          description: 'Delete a specific highlight'
        }
      ],
      bad: []
    },
    
    handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
      try {
        const { highlightId } = params;
        
        // Get PDFium store
        const activeWindowId = context.spaceContext?.activeWindowId;
        const spaceId = context.spaceId;
        
        if (!activeWindowId || !spaceId) {
          return createErrorResult('No active PDF window found', 'No active PDF window in context', 'execution');
        }
        
        const pdfiumStore = getPDFiumStore(activeWindowId, spaceId);
        const store = pdfiumStore.getState();
        
        // Delete highlight
        await store.actions.deleteHighlight(highlightId);
        
        return createSuccessResult('Highlight deleted successfully', {
          highlightId
        });
      } catch (error: any) {
        log.error('[pdf_deleteHighlight] Error:', error);
        return createErrorResult('Failed to delete highlight', error.message || String(error), 'execution');
      }
    }
  } as SelfContainedCommand,
  {
    id: 'pdf_getPage',
    description: 'Get the full text content from a specific page in a PDF document',
    autonomyLevel: 'auto',
    visualTreatment: 'narrative',
    
    schema: {
      parameters: [
        {
          name: 'pageNumber',
          type: 'number',
          description: 'The number of the page to get the text from (1-based)',
          required: true,
          validator: (value: any) => {
            if (typeof value !== 'number' || value < 1 || !Number.isInteger(value)) {
              return 'Page number must be a positive integer starting from 1';
            }
            return null;
          },
          examples: [1, 2, 3, 5, 10]
        }
      ],
      contextRequirements: {
        requiresActiveWindow: false
      }
    },
    
    examples: {
      good: [
        {
          input: { pageNumber: 1 },
          expectedOutput: {
            status: 'success',
            message: '✅ Retrieved page 1 content (5000 characters)',
            format: 'json',
            data: { text: 'Page content...', pageNumber: 1, length: 5000, type: 'pdf_page_content' }
          } as StandardToolResult,
          description: 'Get content from page 1 of a PDF',
          context: 'When PDF is open and enriched'
        }
      ],
      bad: [
        {
          input: { pageNumber: 0 },
          error: 'Page number must be a positive integer starting from 1',
          why: 'Page numbers start from 1, not 0',
          fix: 'Use pageNumber: 1 or higher'
        }
      ]
    },
    
    performance: {
      expectedDuration: 500,
      complexity: 'low',
      memoryIntensive: false
    },
    
    handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
      const startTime = Date.now();
      
      try {
        log.log(`[pdf_getPage] Handler called with params:`, params);
        log.log(`[pdf_getPage] Context structure:`, {
          hasContext: !!context,
          hasSpaceContext: !!(context as any)?.spaceContext,
          spaceId: context?.spaceId,
          contextKeys: Object.keys(context || {}),
          spaceContextKeys: Object.keys((context as any)?.spaceContext || {})
        });
        
        const { pageNumber } = params;
        const spaceId = context.spaceId;
        
        // Find PDF window from context
        const entityRefs = (context.spaceContext as any)?.entityRefs || {};
        
        // Debug: Log context for better troubleshooting
        log.log(`[pdf_getPage] Context debug:`, {
          hasEntityRefs: !!entityRefs,
          entityRefsCount: Object.keys(entityRefs).length,
          entityRefsKeys: Object.keys(entityRefs)
        });
        
        // Log each window individually
        Object.entries(entityRefs).forEach(([windowId, windowContent]: [string, any]) => {
          log.log(`[pdf_getPage] Window ${windowId}:`, {
            appType: windowContent.appType,
            entityType: windowContent.entityType,
            entityName: windowContent.entityName || windowContent.title,
            hasEntityId: !!windowContent.entityId
          });
        });
        
        const pdfWindows = Object.entries(entityRefs).filter(([windowId, windowContent]: [string, any]) => {
          const appType = (windowContent.appType || '').toLowerCase();
          const entityType = (windowContent.entityType || '').toLowerCase();
          
          // Check appType (should be 'pdfium' for PDF documents, but might be 'file.document.pdf')
          const isAppTypePdf = appType === 'pdfium' || 
                              appType === 'pdf' || 
                              appType === 'file.document.pdf' ||
                              appType.includes('pdf');
          
          // Check entityType (could be 'PDF', 'File.Document.PDF', etc.)
          const isEntityTypePdf = entityType === 'pdf' || 
                                 entityType === 'file.document.pdf' || 
                                 entityType.startsWith('file.document.pdf') ||
                                 entityType.includes('pdf');
          
          // Also check title/name for PDF files
          const title = (windowContent.title || windowContent.entityName || '').toLowerCase();
          const isTitlePdf = title.includes('.pdf');
          
          log.log(`[pdf_getPage] Checking window ${windowId}:`, {
            appType,
            entityType,
            title,
            isAppTypePdf,
            isEntityTypePdf,
            isTitlePdf,
            willInclude: isAppTypePdf || isEntityTypePdf || isTitlePdf
          });
          
          return isAppTypePdf || isEntityTypePdf || isTitlePdf;
        });
        
        log.log(`[pdf_getPage] Found ${pdfWindows.length} PDF windows, requesting page ${pageNumber}`);
        
        if (pdfWindows.length === 0) {
          // Enhanced error reporting to help debug the issue
          const debugInfo = {
            availableWindows: Object.keys(entityRefs),
            availableAppTypes: Object.values(entityRefs).map((w: any) => w.appType),
            availableEntityTypes: Object.values(entityRefs).map((w: any) => w.entityType),
            windowDetails: Object.entries(entityRefs).map(([id, content]: [string, any]) => ({
              windowId: id,
              appType: content.appType,
              entityType: content.entityType,
              entityName: content.entityName || content.title
            }))
          };
          
          log.error(`[pdf_getPage] No PDF windows found. Debug info:`, debugInfo);
          
          return createErrorResult(
            'No PDF windows found',
            'No PDF documents found in context. Please open a PDF document first.',
            'validation',
            { 
              data: debugInfo,
              metadata: { executionTime: Date.now() - startTime } 
            }
          );
        }
        
        // Use the first PDF ref found (keys are llmIds in entityRefs)
        const [resolvedLlmId, windowContent] = pdfWindows[0];
        const windowId = resolvedLlmId; // Treat llmId as stable window key for store scoping

        // Resolve entityId from space store using llmId
        const spaceStoreState = useSpaceStore.getState();
        const entityFromStore = (spaceStoreState as any).entities?.[resolvedLlmId];
        const entityId = (entityFromStore?._id || entityFromStore?.id || (windowContent as any).entityId) as string | undefined;
        
        if (!entityId) {
          return createErrorResult(
            'No entity ID',
            'Could not resolve entity for the selected PDF reference',
            'validation',
            { metadata: { executionTime: Date.now() - startTime }, data: { llmId: resolvedLlmId } }
          );
        }

        // Get and initialize the PDF store
        const pdfiumStore = getPDFiumStore(windowId, spaceId);
        const pdfState = pdfiumStore.getState();
        
        if (!pdfState.initialized || pdfState.entityId !== entityId) {
          log.log(`[pdf_getPage] Initializing PDF store for entity: ${entityId}`);
          await pdfiumStore.getState().initialize(entityId);
        }
        
        // Get the latest state after initialization
        const updatedState = pdfiumStore.getState();
        
        // Get the page text from the store
        const pageText = updatedState.getPageText(pageNumber);
        
        if (!pageText) {
          log.error(`[pdf_getPage] No text found for page ${pageNumber}`);
          return createErrorResult(
            'Page not found',
            `No text found for page ${pageNumber}. The page may not exist or the PDF may not be enriched yet.`,
            'execution',
            {
              data: {
                hasEntity: !!updatedState.entity,
                pageContentCount: Object.keys(updatedState.pageContents).length,
                hasEnrichments: updatedState.hasEnrichments,
                enrichmentError: updatedState.enrichmentError,
                enrichmentStatus: updatedState.enrichmentStatus,
                requestedPage: pageNumber,
                suggestion: 'Try enriching the PDF first if it hasn\'t been processed yet'
              },
              metadata: { executionTime: Date.now() - startTime }
            }
          );
        }
        
        // Return the page text with structured formatting
        const structuredContent = `# Page ${pageNumber} - ${(windowContent as any).entityName || 'PDF Document'}\n\n${pageText}`;
        
        return createSuccessResult(
          `✅ Retrieved page ${pageNumber} content (${pageText.length} characters)`,
          {
            text: pageText,
            pageNumber,
            length: pageText.length,
            structuredContent,
            type: 'pdf_page_content'
          },
          {
            format: 'json',
            metadata: {
              executionTime: Date.now() - startTime,
              affectedEntities: [windowId],
              method: 'pdf_getPage'
            } as any
          }
        );
        
      } catch (error: unknown) {
        log.error(`[pdf_getPage] Error accessing page data:`, error);
        log.error(`[pdf_getPage] Error details:`, {
          errorType: typeof error,
          errorConstructor: error?.constructor?.name,
          errorMessage: error instanceof Error ? error.message : String(error),
          errorStack: error instanceof Error ? error.stack : undefined,
          params,
          contextAvailable: !!context,
          spaceContextAvailable: !!(context as any)?.spaceContext
        });
        
        const errorMessage = error instanceof Error ? error.message : String(error);
        
        return createErrorResult(
          'PDF page access failed',
          `Failed to access page ${params.pageNumber}: ${errorMessage}`,
          'execution',
          { 
            data: {
              pageNumber: params.pageNumber,
              errorStack: error instanceof Error ? error.stack : undefined,
              errorType: typeof error,
              errorConstructor: error?.constructor?.name
            },
            metadata: { executionTime: Date.now() - startTime }
          }
        );
      }
    }
  },
  {
    id: 'pdf_getPageChunk',
    description: 'Get a specific chunk of text from a PDF page to avoid token limits. Useful for reading large pages in smaller portions.',
    parameters: [
      {
        name: 'windowId',
        type: 'string',
        description: 'The ID of the window containing the PDF',
        required: true
      },
      {
        name: 'pageNumber',
        type: 'number',
        description: 'The number of the page to get the text from',
        required: true
      },
      {
        name: 'chunkIndex',
        type: 'number',
        description: 'The index of the chunk to retrieve (0-based)',
        required: true
      },
      {
        name: 'chunkSize',
        type: 'number',
        description: 'Size of each chunk in characters (default: 1000)',
        required: false
      },
      {
        name: 'overlap',
        type: 'number', 
        description: 'Overlap between chunks in characters (default: 100)',
        required: false
      }
    ],
    handler: async ({windowId, pageNumber, chunkIndex, chunkSize = 1000, overlap = 100, callback, context}: {
      windowId: string, 
      pageNumber: number, 
      chunkIndex: number,
      chunkSize?: number,
      overlap?: number,
      callback: (text: string) => void, 
      context: SpaceContext
    }) => {
      log.log(`[pdf_getPageChunk] Looking for window: ${windowId}, page: ${pageNumber}, chunk: ${chunkIndex}`);
      
      const windowContent = ((context as any).entityRefs || context.windowContents || {})[windowId];
      if (!windowContent) {
        callback(JSON.stringify({error: "Window not found", windowId}));
        return;
      }
      
      const appType = (windowContent.appType || '').toLowerCase();
      const entityType = (windowContent.entityType || '').toLowerCase();
      
      // Check if this is a PDF window
      const isAppTypePdf = appType === 'pdfium' || 
                          appType === 'pdf' || 
                          appType === 'file.document.pdf' ||
                          appType.includes('pdf');
      const isEntityTypePdf = entityType === 'pdf' || 
                             entityType === 'file.document.pdf' || 
                             entityType.startsWith('file.document.pdf') ||
                             entityType.includes('pdf');
      
      // Also check title for PDF files
      const title = (windowContent.title || windowContent.entityName || '').toLowerCase();
      const isTitlePdf = title.includes('.pdf');
      
      if (!isAppTypePdf && !isEntityTypePdf && !isTitlePdf) {
        callback(JSON.stringify({error: "Window is not a PDF", foundAppType: windowContent.appType, foundEntityType: windowContent.entityType, foundTitle: title}));
        return;
      }
      
      const entityId = windowContent.entityId;
      if (!entityId) {
        callback(JSON.stringify({error: "No entity ID in window"}));
        return;
      }
      
      try {
        const spaceStore = useSpaceStore.getState();
        const spaceId = spaceStore.activeSpace?.id;
        if (!spaceId) {
          throw new Error("No active space found");
        }
        
        const pdfiumStore = getPDFiumStore(windowId, spaceId);
        const pdfState = pdfiumStore.getState();
        
        if (!pdfState.initialized || pdfState.entityId !== entityId) {
          await pdfiumStore.getState().initialize(entityId);
        }
        
        const updatedState = pdfiumStore.getState();
        const pageText = updatedState.getPageText(pageNumber);
        
        if (!pageText) {
          callback(JSON.stringify({error: `No text found for page ${pageNumber}`}));
          return;
        }
        
        // Chunk the text
        const chunks = chunkText(pageText, chunkSize, overlap);
        
        if (chunkIndex >= chunks.length) {
          callback(JSON.stringify({
            error: `Chunk index ${chunkIndex} out of range. Page has ${chunks.length} chunks.`,
            totalChunks: chunks.length,
            pageLength: pageText.length
          }));
          return;
        }
        
        const chunk = chunks[chunkIndex];
        callback(JSON.stringify({
          text: chunk,
          chunkIndex,
          totalChunks: chunks.length,
          chunkSize: chunk.length,
          pageNumber,
          isComplete: chunkIndex === chunks.length - 1
        }));
        
      } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        callback(JSON.stringify({error: `Failed to access page chunk: ${errorMessage}`}));
      }
    }
  },
  {
    id: 'pdf_searchText',
    description: 'Search for specific text within a PDF page and return matching sections with context',
    autonomyLevel: 'auto',
    visualTreatment: 'demonstrative',
    
    schema: {
      parameters: [
        {
          name: 'pageNumber',
          type: 'number',
          description: 'The number of the page to search in (1-based)',
          required: true,
          validator: (value: any) => {
            if (typeof value !== 'number' || value < 1 || !Number.isInteger(value)) {
              return 'Page number must be a positive integer starting from 1';
            }
            return null;
          },
          examples: [1, 2, 5, 10]
        },
        {
          name: 'searchTerm',
          type: 'string',
          description: 'The text to search for (case-insensitive)',
          required: true,
          validator: (value: any) => {
            if (typeof value !== 'string' || value.trim().length === 0) {
              return 'Search term must be a non-empty string';
            }
            if (value.length > 500) {
              return 'Search term must be less than 500 characters';
            }
            return null;
          },
          examples: ['introduction', 'conclusion', 'methodology', 'results']
        },
        {
          name: 'contextSize',
          type: 'number',
          description: 'Amount of context around each match in characters (default: 200)',
          required: false,
          defaultValue: 200,
          validator: (value: any) => {
            if (value !== undefined && (typeof value !== 'number' || value < 0 || value > 1000)) {
              return 'Context size must be between 0 and 1000 characters';
            }
            return null;
          },
          examples: [100, 200, 300, 500]
        }
      ],
      contextRequirements: {
        requiresActiveWindow: true
      }
    },
    
    examples: {
      good: [
        {
          input: { pageNumber: 1, searchTerm: 'introduction', contextSize: 200 },
          expectedOutput: {
            status: 'success',
            message: '✅ Found 2 matches for "introduction" on page 1',
            format: 'json',
            data: { searchTerm: 'introduction', pageNumber: 1, matchCount: 2, matches: [] }
          } as StandardToolResult,
          description: 'Search for "introduction" with default context',
          context: 'When searching for text that exists on the page'
        }
      ],
      bad: [
        {
          input: { pageNumber: 1, searchTerm: '' },
          error: 'Search term must be a non-empty string',
          why: 'Empty search terms are not valid',
          fix: 'Provide a non-empty search term'
        }
      ]
    },
    
    performance: {
      expectedDuration: 400,
      complexity: 'low',
      memoryIntensive: false
    },
    
    handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
      const startTime = Date.now();
      
      try {
        const { pageNumber, searchTerm, contextSize = 200 } = params;
        
        // Context provides windowId and spaceId automatically
        let windowId = context.spaceContext.activeWindowId as string | undefined;
        const spaceId = context.spaceId;
        
        log.log(`[pdf_searchText] Searching for "${searchTerm}" in window: ${windowId}, page: ${pageNumber}`);
        
        // Step 1: Validate we have a window; if missing, infer from entityRefs when a single PDF ref exists
        if (!windowId) {
          const entityRefs = (context.spaceContext as any)?.entityRefs || {};
          const pdfEntries = Object.entries(entityRefs).filter(([_, ref]: [string, any]) => {
            const t = (ref?.appType || '').toString().toLowerCase();
            return t === 'pdfium' || t === 'pdf' || t === 'file.document.pdf' || t.includes('pdf');
          });
          if (pdfEntries.length === 1) {
            (context.spaceContext as any).activeWindowId = pdfEntries[0][0];
            windowId = pdfEntries[0][0];
          }
          if (!windowId) {
            return createErrorResult(
              'No active window',
              'No active PDF document found. Please add a PDF to context or open it.',
              'validation',
              { 
                metadata: { executionTime: Date.now() - startTime },
                data: { entityRefKeys: Object.keys(entityRefs) }
              }
            );
          }
        }
        
        // Step 2: Find the window content
        const windowContent = (context.spaceContext as any)?.entityRefs?.[windowId];
        if (!windowContent) {
          return createErrorResult(
            'Window not found',
            `Window ${windowId} not found in context`,
            'validation',
            { 
              data: { 
                requestedWindowId: windowId,
                availableWindows: Object.keys(((context.spaceContext as any).entityRefs || {}))
              },
              metadata: { executionTime: Date.now() - startTime }
            }
          );
        }
        
        // Step 3: Check window type (accept both 'PDF' and 'pdfium')
        const appType = (windowContent.appType || '').toLowerCase();
        const entityType = (windowContent.entityType || '').toLowerCase();
        
        // Check if this is a PDF window
        const isAppTypePdf = appType === 'pdfium' || 
                            appType === 'pdf' || 
                            appType === 'file.document.pdf' ||
                            appType.includes('pdf');
        const isEntityTypePdf = entityType === 'pdf' || 
                               entityType === 'file.document.pdf' || 
                               entityType.startsWith('file.document.pdf') ||
                               entityType.includes('pdf');
        
        // Also check title for PDF files
        const title = (windowContent.title || windowContent.entityName || '').toLowerCase();
        const isTitlePdf = title.includes('.pdf');
        
        if (!isAppTypePdf && !isEntityTypePdf && !isTitlePdf) {
          return createErrorResult(
            'Invalid window type',
            'The active window is not a PDF document',
            'validation',
            {
              data: {
                foundAppType: windowContent.appType,
                foundEntityType: windowContent.entityType,
                foundTitle: title,
                requiredTypes: ['pdfium', 'PDF', 'File.Document.PDF']
              },
              metadata: { executionTime: Date.now() - startTime }
            }
          );
        }
        
        // Step 4: Get entity ID from window content
        const entityId = windowContent.entityId;
        if (!entityId) {
          return createErrorResult(
            'No entity ID',
            'PDF window does not have an associated entity',
            'validation',
            { metadata: { executionTime: Date.now() - startTime } }
          );
        }
        
        // Step 5: Get and initialize the PDF store
        const pdfiumStore = getPDFiumStore(windowId, spaceId);
        const pdfState = pdfiumStore.getState();
        
        if (!pdfState.initialized || pdfState.entityId !== entityId) {
          await pdfiumStore.getState().initialize(entityId);
        }
        
        const updatedState = pdfiumStore.getState();
        const pageText = updatedState.getPageText(pageNumber);
        
        if (!pageText) {
          return createErrorResult(
            'Page not found',
            `No text found for page ${pageNumber}. The page may not exist or may not have been processed yet.`,
            'execution',
            {
              data: {
                hasEntity: !!updatedState.entity,
                pageContentCount: Object.keys(updatedState.pageContents).length,
                requestedPage: pageNumber
              },
              metadata: { executionTime: Date.now() - startTime }
            }
          );
        }
        
        // Search for text occurrences
        const matches = findTextOccurrences(pageText, searchTerm, contextSize);
        
        const searchResults = {
          searchTerm,
          pageNumber,
          matchCount: matches.length,
          matches: matches.map((match, index) => ({
            index,
            position: match.position,
            text: match.text,
            contextSize
          }))
        };
        
        return createSuccessResult(
          `✅ Found ${matches.length} matches for "${searchTerm}" on page ${pageNumber}`,
          searchResults,
          {
            format: 'json',
            metadata: {
              executionTime: Date.now() - startTime,
              affectedEntities: [windowId]
            },
            ui: {
              refreshContext: false,
              autoFocus: windowId
            }
          }
        );
        
      } catch (error: unknown) {
        log.error(`[pdf_searchText] Error searching text:`, error);
        const errorMessage = error instanceof Error ? error.message : String(error);
        
        return createErrorResult(
          'PDF search operation failed',
          `Failed to search text: ${errorMessage}`,
          'execution',
          { 
            data: {
              pageNumber: params.pageNumber,
              searchTerm: params.searchTerm,
              errorStack: error instanceof Error ? error.stack : undefined
            },
            metadata: { executionTime: Date.now() - startTime }
          }
        );
      }
    }
  } as SelfContainedCommand,
  {
    id: 'pdf_getPageRange',
    description: 'Get text from a range of characters within a PDF page',
    parameters: [
      {
        name: 'windowId',
        type: 'string',
        description: 'The ID of the window containing the PDF',
        required: true
      },
      {
        name: 'pageNumber',
        type: 'number',
        description: 'The number of the page to get text from',
        required: true
      },
      {
        name: 'startPos',
        type: 'number',
        description: 'Starting character position (0-based)',
        required: true
      },
      {
        name: 'endPos',
        type: 'number',
        description: 'Ending character position (-1 for end of page)',
        required: false
      }
    ],
    handler: async ({windowId, pageNumber, startPos, endPos = -1, callback, context}: {
      windowId: string,
      pageNumber: number,
      startPos: number,
      endPos?: number,
      callback: (text: string) => void,
      context: SpaceContext
    }) => {
      log.log(`[pdf_getPageRange] Getting range ${startPos}-${endPos} from window: ${windowId}, page: ${pageNumber}`);
      
      const windowContent = ((context as any).entityRefs || {})[windowId];
      if (!windowContent) {
        callback(JSON.stringify({error: "Window not found", windowId}));
        return;
      }
      
      const appType = (windowContent.appType || '').toLowerCase();
      const entityType = (windowContent.entityType || '').toLowerCase();
      
      // Check if this is a PDF window
      const isAppTypePdf = appType === 'pdfium' || 
                          appType === 'pdf' || 
                          appType === 'file.document.pdf' ||
                          appType.includes('pdf');
      const isEntityTypePdf = entityType === 'pdf' || 
                             entityType === 'file.document.pdf' || 
                             entityType.startsWith('file.document.pdf') ||
                             entityType.includes('pdf');
      
      // Also check title for PDF files
      const title = (windowContent.title || windowContent.entityName || '').toLowerCase();
      const isTitlePdf = title.includes('.pdf');
      
      if (!isAppTypePdf && !isEntityTypePdf && !isTitlePdf) {
        callback(JSON.stringify({error: "Window is not a PDF", foundAppType: windowContent.appType, foundEntityType: windowContent.entityType, foundTitle: title}));
        return;
      }
      
      const entityId = windowContent.entityId;
      if (!entityId) {
        callback(JSON.stringify({error: "No entity ID in window"}));
        return;
      }
      
      try {
        const spaceStore = useSpaceStore.getState();
        const spaceId = spaceStore.activeSpace?.id;
        if (!spaceId) {
          throw new Error("No active space found");
        }
        
        const pdfiumStore = getPDFiumStore(windowId, spaceId);
        const pdfState = pdfiumStore.getState();
        
        if (!pdfState.initialized || pdfState.entityId !== entityId) {
          await pdfiumStore.getState().initialize(entityId);
        }
        
        const updatedState = pdfiumStore.getState();
        const pageText = updatedState.getPageText(pageNumber);
        
        if (!pageText) {
          callback(JSON.stringify({error: `No text found for page ${pageNumber}`}));
          return;
        }
        
        // Validate and adjust positions
        const safeStartPos = Math.max(0, Math.min(startPos, pageText.length));
        const safeEndPos = endPos === -1 ? pageText.length : Math.max(safeStartPos, Math.min(endPos, pageText.length));
        
        const textRange = pageText.slice(safeStartPos, safeEndPos);
        
        callback(JSON.stringify({
          text: textRange,
          startPos: safeStartPos,
          endPos: safeEndPos,
          length: textRange.length,
          pageNumber,
          totalPageLength: pageText.length
        }));
        
      } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        callback(JSON.stringify({error: `Failed to get page range: ${errorMessage}`}));
      }
    }
  },
  {
    id: 'pdf_getSummaries',
    description: 'Get the summaries of a PDF from pdfium app with comprehensive validation and error handling',
    autonomyLevel: 'auto',
    visualTreatment: 'narrative',
    
    schema: {
      parameters: [
        {
          name: 'expand',
          type: 'string',
          description: 'Brief description of what SPECIFICALLY was completed by the tool',
          required: false,
          examples: ['Retrieved PDF summaries', 'Loaded page summaries from PDF']
        },
        {
          name: 'displayAction',
          type: 'string',
          description: 'Text shown to user while the tool is executing',
          required: false,
          examples: ['Getting PDF summaries...', 'Loading page summaries...']
        }
      ],
      contextRequirements: {
        requiresActiveWindow: false  // Don't require active window - we'll find the PDF from context
      }
    },
    
    examples: {
      good: [
        {
          input: {
            expand: 'Retrieved PDF summaries',
            displayAction: 'Getting PDF summaries...'
          },
          expectedOutput: {
            status: 'success',
            message: '✅ Successfully retrieved PDF summaries',
            format: 'json',
            data: { summaries: { '1': 'Page 1 summary...', '2': 'Page 2 summary...' } }
          } as StandardToolResult,
          description: 'Get summaries from a loaded PDF',
          context: 'When PDF window is active and summaries are available'
        }
      ],
      bad: [
        {
          input: {},
          error: 'No active PDF window found',
          why: 'No PDF window is open or active',
          fix: 'Open a PDF document first'
        }
      ]
    },
    
    performance: {
      expectedDuration: 1000,
      complexity: 'medium',
      memoryIntensive: false
    },
    
    handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
      const startTime = Date.now();
      
      try {
        log.log(`[pdf_getSummaries] Handler called with params:`, params);
        log.log(`[pdf_getSummaries] Context structure:`, {
          hasContext: !!context,
          hasSpaceContext: !!(context as any)?.spaceContext,
          spaceId: context?.spaceId,
          contextKeys: Object.keys(context || {}),
          spaceContextKeys: Object.keys((context as any)?.spaceContext || {})
        });
        
        const { expand = 'Retrieved PDF summaries', displayAction = 'Getting PDF summaries...' } = params;
        
        const spaceId = context.spaceId;
        
        // Step 1: Find PDF window from context
        const entityRefs = (context.spaceContext as any)?.entityRefs || {};
        
        // Debug: Log what's actually in the context
        log.log(`[pdf_getSummaries] Context analysis:`, {
          hasEntityRefs: !!entityRefs,
          entityRefsKeys: Object.keys(entityRefs),
          entityRefsCount: Object.keys(entityRefs).length
        });
        
        // Log each window individually for better debugging
        Object.entries(entityRefs).forEach(([windowId, windowContent]: [string, any]) => {
          log.log(`[pdf_getSummaries] Window ${windowId}:`, {
            appType: windowContent.appType,
            entityType: windowContent.entityType,
            entityName: windowContent.entityName || windowContent.title,
            hasEntityId: !!windowContent.entityId
          });
        });
        
        const pdfWindows = Object.entries(entityRefs).filter(([windowId, windowContent]: [string, any]) => {
          const appType = (windowContent.appType || '').toLowerCase();
          const entityType = (windowContent.entityType || '').toLowerCase();
          
          // Check appType (should be 'pdfium' for PDF documents, but might be 'file.document.pdf')
          const isAppTypePdf = appType === 'pdfium' || 
                              appType === 'pdf' || 
                              appType === 'file.document.pdf' ||
                              appType.includes('pdf');
          
          // Check entityType (could be 'PDF', 'File.Document.PDF', etc.)
          const isEntityTypePdf = entityType === 'pdf' || 
                                 entityType === 'file.document.pdf' || 
                                 entityType.startsWith('file.document.pdf') ||
                                 entityType.includes('pdf');
          
          // Also check title/name for PDF files
          const title = (windowContent.title || windowContent.entityName || '').toLowerCase();
          const isTitlePdf = title.includes('.pdf');
          
          log.log(`[pdf_getSummaries] Checking window ${windowId}:`, {
            appType,
            entityType,
            title,
            isAppTypePdf,
            isEntityTypePdf,
            isTitlePdf,
            willInclude: isAppTypePdf || isEntityTypePdf || isTitlePdf
          });
          
          return isAppTypePdf || isEntityTypePdf || isTitlePdf;
        });
        
        log.log(`[pdf_getSummaries] Found ${pdfWindows.length} PDF windows in context`);
        
        if (pdfWindows.length === 0) {
          // Enhanced error reporting to help debug the issue
          const debugInfo = {
            availableWindows: Object.keys(entityRefs),
            availableAppTypes: Object.values(entityRefs).map((w: any) => w.appType),
            availableEntityTypes: Object.values(entityRefs).map((w: any) => w.entityType),
            windowDetails: Object.entries(entityRefs).map(([id, content]: [string, any]) => ({
              windowId: id,
              appType: content.appType,
              entityType: content.entityType,
              entityName: content.entityName || content.title
            }))
          };
          
          log.error(`[pdf_getSummaries] No PDF windows found. Debug info:`, debugInfo);
          
          return createErrorResult(
            'No PDF windows found',
            'No PDF documents found in context. Please open a PDF document first.',
            'validation',
            { 
              data: debugInfo,
              metadata: { executionTime: Date.now() - startTime } 
            }
          );
        }
        
        // Use the first PDF ref found (keys are llmIds in entityRefs)
        const [resolvedLlmId, windowContent] = pdfWindows[0];
        const windowId = resolvedLlmId; // Treat llmId as stable window key for store scoping
        
        log.log(`[pdf_getSummaries] Using PDF window: ${windowId}`);
        
        // Step 2: Resolve entity ID using llmId key (entityRefs are keyed by llmId)
        const spaceStoreState = useSpaceStore.getState();
        const entityFromStore = (spaceStoreState as any).entities?.[resolvedLlmId];
        const entityId = (entityFromStore?._id || entityFromStore?.id || (windowContent as any).entityId) as string | undefined;
        if (!entityId) {
          return createErrorResult(
            'No entity ID',
            'Could not resolve entity for the selected PDF reference',
            'validation',
            { metadata: { executionTime: Date.now() - startTime }, data: { llmId: resolvedLlmId } }
          );
        }
        
        // Step 3: Get and initialize the PDF store
        const pdfiumStore = getPDFiumStore(windowId, spaceId);
        const pdfState = pdfiumStore.getState();
        
        if (!pdfState.initialized || pdfState.entityId !== entityId) {
          log.log(`[pdf_getSummaries] Initializing PDF store for entity: ${entityId}`);
          await pdfiumStore.getState().initialize(entityId);
        }
        
        // Get the latest state after initialization
        const updatedState = pdfiumStore.getState();
        
        // Get the summaries from the store
        const summaries = updatedState.getAllPageSummaries();
        
        log.log(`[pdf_getSummaries] Retrieved summaries:`, {
          summaryCount: Object.keys(summaries).length,
          summaryKeys: Object.keys(summaries).slice(0, 5),
          hasEnrichments: updatedState.hasEnrichments,
          enrichmentStatus: updatedState.enrichmentStatus
        });
        
        if (Object.keys(summaries).length === 0) {
          return createErrorResult(
            'No summaries available',
            'Failed to retrieve PDF summaries. The PDF may not have been enriched yet. Try enriching the PDF first.',
            'execution',
            {
              data: {
                hasEntity: !!updatedState.entity,
                hasEnrichments: updatedState.hasEnrichments,
                enrichmentError: updatedState.enrichmentError,
                enrichmentStatus: updatedState.enrichmentStatus,
                entityId,
                suggestion: 'Use the Enrich button in the PDF viewer to process the PDF content first'
              },
              metadata: { executionTime: Date.now() - startTime }
            }
          );
        }
        
        // Create structured content with the summaries
        let structuredContent = `# PDF Summary: ${(windowContent as any).entityName || 'PDF Document'}\n\n`;
        
        Object.entries(summaries).forEach(([pageNumber, summary]) => {
          structuredContent += `## Page ${pageNumber}\n\n${summary}\n\n`;
        });
        
        // Return the summaries
        return createSuccessResult(
          `✅ Successfully retrieved ${Object.keys(summaries).length} page summaries from PDF`,
          { 
            summaries,
            structuredContent,
            pageCount: Object.keys(summaries).length,
            type: 'pdf_summaries'
          },
          {
            format: 'json',
            metadata: {
              executionTime: Date.now() - startTime,
              affectedEntities: [windowId],
              method: 'pdf_getSummaries'
            } as any,
            ui: {
              refreshContext: false,
              autoFocus: windowId
            }
          }
        );
        
      } catch (error: unknown) {
        log.error(`[pdf_getSummaries] Error accessing summaries:`, error);
        log.error(`[pdf_getSummaries] Error details:`, {
          errorType: typeof error,
          errorConstructor: error?.constructor?.name,
          errorMessage: error instanceof Error ? error.message : String(error),
          errorStack: error instanceof Error ? error.stack : undefined,
          params,
          contextAvailable: !!context,
          spaceContextAvailable: !!(context as any)?.spaceContext
        });
        
        const errorMessage = error instanceof Error ? error.message : String(error);
        
        return createErrorResult(
          'PDF summaries operation failed',
          `Failed to access summaries: ${errorMessage}`,
          'execution',
          { 
            data: {
              errorStack: error instanceof Error ? error.stack : undefined,
              errorType: typeof error,
              errorConstructor: error?.constructor?.name
            },
            metadata: { executionTime: Date.now() - startTime }
          }
        );
      }
    }
  } as SelfContainedCommand,
];
